/**
 * Adapts the flat `PublicRosterRow[]` a public share-view payload carries
 * into the shape ScriptsTab's columns read.
 *
 * A SIBLING of CreatorsTab's (`CreatorsTab/publicRosterRowAdapter.ts`) and
 * Confirmed's (`ConfirmedTab.publicRosterRowAdapter.ts`) adapters — reuses
 * `publicRosterRowSharedFields.ts`'s identity mapping (id/user_id/platform),
 * the one piece genuinely common to all three, and otherwise diverges: this
 * tab's target type (`RosterEntryScript`) has no `snapshot_metrics` or
 * `deliverables` field at all, so those two shared helpers don't apply here.
 *
 * **Why `status` is a frozen, never-real placeholder (unlike Confirmed's
 * frozen `accepted_by_creator`):** the BE's `share_view_page.go` builds every
 * roster tab's public row via the SAME `buildPublicRosterRow(entry)`, so
 * `PublicRosterRow.status` is ALWAYS the roster ENTRY's negotiation status
 * (e.g. `accepted_by_creator`), never the production POST's review status
 * (`script_pending` / `script_review`) — there is no separate field on the
 * wire for that at all. `buildProductionRosterView` (the builder Scripts
 * routes through) only changes WHICH entries appear on a granted page
 * (filtered by roster_posts status membership); it does not add a per-row
 * script-status field. Unlike Confirmed's freeze (which is safe because
 * EVERY Confirmed-tab row really is `accepted_by_creator`, always), a Scripts
 * row's true script status varies per row and per grant, and this adapter has
 * no way to know it — so freezing to a *specific* value here would be
 * actively misleading if ever read for real rendering. The placeholder exists
 * purely to satisfy `RosterEntryScript.status`'s type structurally.
 *
 * Consequently `ScriptsTab.tsx` must force the Status / Last comment /
 * Updated / Action cells to a permanent skeleton off the component-level
 * `isPublicView` flag for EVERY row, never off this adapter's placeholder
 * values (`status`, `status_updated_at`, `last_message`, `unread_count`,
 * `script_thread_locked`, `content_thread_locked`) — none of those fields
 * has a wire representation, ever, at any point on the BE's builder path
 * today. Only the Creator column (platform identity) reads real data.
 */
import type { PublicRosterRow } from '@discovr/features';
import type { RosterEntryScript } from '@discovr/types';
import { adaptPublicRosterRowIdentity } from './publicRosterRowSharedFields';

/** Adapted row shape, structurally close to `RosterEntryScript` but with
 *  every production-phase field forced to an inert placeholder — see this
 *  file's header for why none of them (besides `platform`) can ever be
 *  anything but a placeholder here. */
export interface PublicScriptRow {
  id: string;
  user_id: string;
  status: 'script_pending' | 'script_review';
  status_updated_at: string;
  platform: {
    username: string;
    name: string;
    profile_picture_url: string;
    is_verified: boolean;
  };
  last_message?: undefined;
  unread_count: number;
  script_thread_locked: boolean;
  content_thread_locked: boolean;
  timestamps: { created_at: string; updated_at: string };
}

function adaptPublicRosterRow(row: PublicRosterRow): PublicScriptRow {
  return {
    ...adaptPublicRosterRowIdentity(row),
    // Frozen placeholder — see this file's header. Never read for real
    // rendering: ScriptsTab.tsx's Status/Action cells force a skeleton off
    // `isPublicView`, not off this value.
    status: 'script_pending',
    status_updated_at: '',
    last_message: undefined,
    unread_count: 0,
    script_thread_locked: true,
    content_thread_locked: true,
    timestamps: { created_at: '', updated_at: '' },
  };
}

export function adaptPublicRosterRows(rows: PublicRosterRow[]): PublicScriptRow[] {
  return rows.map(adaptPublicRosterRow);
}

/** Cast helper for the one call site (ScriptsTab.tsx) that must feed these
 *  rows into `DataTable<RosterEntryScript>`. Kept as a named export (rather
 *  than an inline `as unknown as` at the call site) so the type gap is
 *  documented in exactly one place — mirrors
 *  `adaptPublicRosterRowsAsCreatorsRows` / `adaptPublicRosterRowsAsFinalizedRows`
 *  in the sibling adapters. */
export function adaptPublicRosterRowsAsScriptRows(rows: PublicRosterRow[]): RosterEntryScript[] {
  return adaptPublicRosterRows(rows) as unknown as RosterEntryScript[];
}
